import React from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeUiLang, persistManualLanguage } from '../../utils/localePreference';

const LANG_OPTIONS = [
  { code: 'en', flag: '🇬🇧', labelKey: 'home_lang_en' },
  { code: 'sw', flag: '🇹🇿', labelKey: 'home_lang_sw' },
  { code: 'fr', flag: '🇫🇷', labelKey: 'home_lang_fr' },
] as const;

interface CrmLanguagePickerProps {
  /** dark = CRM login landing; light = CRM panel header */
  variant?: 'dark' | 'light';
  className?: string;
}

export default function CrmLanguagePicker({ variant = 'dark', className = '' }: CrmLanguagePickerProps) {
  const { t, i18n } = useTranslation();
  const active = normalizeUiLang(i18n.resolvedLanguage || i18n.language);

  const shellClass =
    variant === 'dark'
      ? 'flex gap-1 rounded-[10px] p-[3px] bg-white/10 backdrop-blur-sm border border-white/10'
      : 'flex gap-1 rounded-lg p-[3px] bg-gray-100 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600';

  return (
    <div className={`${shellClass} ${className}`} role="group" aria-label={t('crmLangPickerLabel')}>
      {LANG_OPTIONS.map((lang) => {
        const selected = active === lang.code;
        const btnClass =
          variant === 'dark'
            ? selected
              ? 'bg-white/95 text-slate-900 shadow-sm'
              : 'bg-transparent text-white/70 hover:text-white/90'
            : selected
              ? 'bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 shadow-sm'
              : 'bg-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white';

        return (
          <button
            key={lang.code}
            type="button"
            title={t(lang.labelKey)}
            onClick={() => {
              persistManualLanguage(lang.code);
              void i18n.changeLanguage(lang.code);
            }}
            className={`flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${btnClass}`}
          >
            <span aria-hidden>{lang.flag}</span>
            <span className="hidden sm:inline">{lang.code.toUpperCase()}</span>
          </button>
        );
      })}
    </div>
  );
}
